import { api_configuration } from "../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import api_views = api_configuration.libraries.gui_definition_lib.types.views.alan_api;
import { getReferencedNode } from "../state/ResolveStatus";
import { findEntryWithKey } from "../state/Collection";

export default function walkNodePath(
	$: api_views.Cnode_path,
	node: api_cs.Cnode,
	onResult: (node: api_cs.Cnode) => api_cs.Cnode = (node: api_cs.Cnode) => node,
	onNoResult: () => undefined = () => undefined
): api_cs.Cnode | undefined {
	switch ($.properties.has_steps.state.name) {
		case "no":
			return onResult(node);
		case "yes": {
			const has_steps = $.properties.has_steps.state.node;
			switch (has_steps.properties.type.state.name) {
				case "parent":
					return walkNodePath(
						has_steps.properties.tail,
						node.input.position().output.parent().cast("node"),
						onResult,
						onNoResult
					);
				case "group": {
					const $ = has_steps.properties.type.cast("group");
					if (node.properties.attributes.has($.properties.group.entry)) {
						const group = node.properties.attributes
							.get($.properties.group.entry)!
							.properties.type.cast("property")
							.properties.type.cast("group");
						return walkNodePath(has_steps.properties.tail, group.properties.node, onResult, onNoResult);
					}
					return onNoResult();
				}
				case "reference": {
					const $ = has_steps.properties.type.state.node;
					const reference = node.properties.attributes
						.get($.properties.text.entry)
						?.properties.type.cast("property")
						.properties.type.cast("text");

					if (reference !== undefined) {
						switch (reference.properties.navigable.state.name) {
							case "no": {
								return onNoResult();
							}
							case "yes": {
								const navigable_reference = reference.properties.navigable.state.node;
								switch (navigable_reference.properties.resolve_status.properties.status.state.name) {
									case "failed to resolve": {
										return onNoResult();
									}
									case "is busy resolving": {
										return onNoResult();
									}
									case "resolved": {
										const resolved = navigable_reference.properties.resolve_status.properties.status.state.node;
										return walkNodePath(has_steps.properties.tail, getReferencedNode(resolved), onResult, onNoResult);
									}
									case "unresolved": {
										return onNoResult();
									}
									default:
										return unexpectedState(navigable_reference.properties.resolve_status.properties.status.state);
								}
							}
							default:
								return unexpectedState(reference.properties.navigable.state);
						}
					} else {
						return onNoResult();
					}
				}
				case "reference rule": {
					const $ = has_steps.properties.type.state.node;
					const reference = node.properties.attributes
						.get($.properties.property.entry)
						?.properties.type.cast("property")
						.properties.type.cast("text");
					if (reference !== undefined) {
						switch (reference.properties.navigable.state.name) {
							case "no": {
								return onNoResult();
							}
							case "yes": {
								const navigable = reference.properties.navigable.state.node;
								const rule = navigable.properties.rules.properties.rules.get($.properties.rule.entry);
								if (rule !== undefined) {
									switch (rule.properties.resolve_status.properties.status.state.name) {
										case "failed to resolve": {
											return onNoResult();
										}
										case "is busy resolving": {
											return onNoResult();
										}
										case "resolved": {
											const resolved = rule.properties.resolve_status.properties.status.state.node;
											return walkNodePath(has_steps.properties.tail, getReferencedNode(resolved), onResult, onNoResult);
										}
										case "unresolved": {
											return onNoResult();
										}
										default:
											return unexpectedState(rule.properties.resolve_status.properties.status.state);
									}
								} else {
									return onNoResult();
								}
							}
							default:
								return unexpectedState(reference.properties.navigable.state);
						}
					} else {
						return onNoResult();
					}
				}
				case "state rule": {
					const $ = has_steps.properties.type.state.node;
					const state = node.input.position().cast("state");
					const rule = state.properties.rules.properties.rules.get($.properties.rule.entry);
					if (rule !== undefined) {
						switch (rule.properties.resolve_status.properties.status.state.name) {
							case "failed to resolve": {
								return onNoResult();
							}
							case "is busy resolving": {
								return onNoResult();
							}
							case "resolved": {
								const resolved = rule.properties.resolve_status.properties.status.state.node;
								return walkNodePath(has_steps.properties.tail, getReferencedNode(resolved), onResult, onNoResult);
							}
							case "unresolved": {
								return onNoResult();
							}
							default:
								return unexpectedState(rule.properties.resolve_status.properties.status.state);
						}
					} else {
						return onNoResult();
					}
				}
				case "state": {
					const $ = has_steps.properties.type.state.node;
					if (node.properties.attributes.has($.properties.state_group.entry)) {
						const state = node.properties.attributes
							.get($.properties.state_group.entry)!
							.properties.type.cast("property")
							.properties.type.cast("state group")
							.properties.states.get($.properties.state.entry)!;
						switch (state.properties.is_selected.state.name) {
							case "no":
								return onNoResult();
							case "yes":
								return walkNodePath(has_steps.properties.tail, state.properties.node, onResult, onNoResult);
							default:
								unexpectedState(state.properties.is_selected.state);
						}
					}
					return onNoResult();
				}
				case "collection entry": {
					const $ = has_steps.properties.type.state.node;
					const collection = node.properties.attributes
						.get($.properties.collection.entry)
						?.properties.type.cast("property")
						.properties.type.cast("collection");
					if (collection !== undefined) {
						const entry = findEntryWithKey(collection, $.properties.entry_key);
						if (entry !== undefined) {
							return walkNodePath(has_steps.properties.tail, entry.properties.node, onResult, onNoResult);
						} else {
							return onNoResult();
						}
					} else {
						return onNoResult();
					}
				}
				default:
					return unexpectedState(has_steps.properties.type.state);
			}
		}
		default:
			return unexpectedState($.properties.has_steps.state);
	}
}
